/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MultipleFileUpload from "../Common/FileUpload/MultipleFileUpload";
import wizardStyle from "../wizardStyle";
import moment from "moment";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { MediaObject } from "../../../types/Entities/MediaObject/MediaObject";
import { filterMediaObjectsByFieldname } from "../../../services/mediaObjects";
import useMediaObjectCombinationRequest from "../../../hooks/useMediaObjectCombinationRequest";
import FileUploadSection from "../Common/FileUploadSection/FileUploadSection";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import { ContractDocumentsProps } from "../../../types/ContractDocuments";

type CaseSpecificDocumentUploadSectionProps = {
  product: AbstractCase;
  mediaObjectCollection: Array<MediaObject>;
  fetchMediaObjectCollection: () => void;
  refreshCase: ContractDocumentsProps["refreshCase"];
  setCaseSpecificSubmitDisabled: (isDisabled: boolean) => void;
};

const CaseSpecificDocumentUploadSection = ({
  product,
  mediaObjectCollection,
  fetchMediaObjectCollection,
  refreshCase,
  setCaseSpecificSubmitDisabled,
}: CaseSpecificDocumentUploadSectionProps) => {
  const [uploadIsBlocked, setUploadIsBlocked] = useState(false);

  let mediaObjectCombinationRequestTypes: string[] = [];

  if (product.caseCategory === "defendClaim") {
    switch (product.defenseProcessStatus) {
      case "paymentOrder":
        mediaObjectCombinationRequestTypes.push("paymentOrder");
        break;
      case "enforcementOrder":
        mediaObjectCombinationRequestTypes.push("enforcementOrder");
        break;
      case "lawsuit":
        mediaObjectCombinationRequestTypes.push("lawsuit");
        break;
      default:
        break;
    }
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, hasAtLeastOneFileOfType, updateAction } = useMediaObjectCombinationRequest(
    product,
    mediaObjectCombinationRequestTypes
  );

  useEffect(() => {
    const utilitiesStatementRequired = product.reasonForDispute === "utilitiesStatement";
    const utilitiesStatementCollection = filterMediaObjectsByFieldname(mediaObjectCollection, "utilitiesStatementDocument");
    setCaseSpecificSubmitDisabled && setCaseSpecificSubmitDisabled(utilitiesStatementRequired && utilitiesStatementCollection.length === 0);
  }, [mediaObjectCollection]);

  const getCaseSpecificSubmitDisabled = () => {
    let isDisabled: boolean = isLoadingMediaObjectCombinationRequest;
    mediaObjectCombinationRequestTypes.forEach((type) => {
      if (!hasAtLeastOneFileOfType(mediaObjectCombinationRequests, type)) {
        isDisabled = true;
      }
    });
    return isDisabled;
  };

  useEffect(() => {
    setCaseSpecificSubmitDisabled(getCaseSpecificSubmitDisabled());
  }, [isLoadingMediaObjectCombinationRequest]);

  if (isLoadingMediaObjectCombinationRequest) {
    return <LegalbirdLoader centered />;
  }

  const postSubmitCallback = async () => {
    await refreshCase();
    setCaseSpecificSubmitDisabled(getCaseSpecificSubmitDisabled());
  };

  return (
    <>
      {["housingDeficiencies"].includes(product.reasonForDispute) && (
        <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
          <Typography variant={"h5"}>Bilder zu vorliegenden Mängeln</Typography>
          <Typography>Es liegen Mängel vor, die Sie mit Bildern darlegen können, dann lassen Sie uns diese bitte zukommen.</Typography>
          <MultipleFileUpload
            product={product}
            fieldname={"housingDeficienciesDocument"}
            description={"Bilder Mängel"}
            mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "housingDeficienciesDocument")}
            fetchMediaObjects={fetchMediaObjectCollection}
          />
        </Grid>
      )}
      {product.reasonForDispute === "deposit" && (
        <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
          <Typography variant={"h5"}>Abnahmeprotokoll von Wohnungsübergabe</Typography>
          <Typography>Liegt Ihnen ein Abnahmeprotokoll von der Wohnungsübergabe vor? Dann laden Sie dieses gerne hier hoch.</Typography>
          <MultipleFileUpload
            product={product}
            fieldname={"acceptanceReportDocument"}
            description={"Abnahmeprotokoll"}
            mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "acceptanceReportDocument")}
            fetchMediaObjects={fetchMediaObjectCollection}
          />
        </Grid>
      )}
      {product.reasonForDispute === "utilitiesStatement" && (
        <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
          <Typography variant={"h5"}>Nebenkostenabrechnung</Typography>
          <Typography>Bitte laden Sie uns hier die Nebenkostenabrechnung in gut lesbarer Qualität und inklusive aller Seiten hoch.</Typography>
          <MultipleFileUpload
            product={product}
            fieldname={"utilitiesStatementDocument"}
            description={"Nebenkostenabrechnung"}
            mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "utilitiesStatementDocument")}
            fetchMediaObjects={fetchMediaObjectCollection}
          />
        </Grid>
      )}
      {product.caseCategory === "defendClaim" && (
        <>
          {product.defenseProcessStatus === "paymentOrder" && (
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FileUploadSection
                productEntity={product}
                temporaryMediaObjectsFieldname={"temporaryFilesToCombine"}
                mediaObjectCombinationRequest={mediaObjectCombinationRequests.paymentOrder}
                setIsBlocked={setUploadIsBlocked}
                isBlocked={uploadIsBlocked}
                product={product.name}
                productEntityId={product.id}
                updateAction={updateAction}
                fieldName={"paymentOrderDocument"}
                postSubmitCallback={postSubmitCallback}
                description={"Mahnbescheid Teildatei"}
                headline={"Mahnbescheid"}
                helperText={"Bitte laden Sie hier Ihren Mahnbescheid inklusive gelbem Umschlag vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden."}
              />
            </Grid>
          )}
          {product.defenseProcessStatus === "enforcementOrder" && (
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FileUploadSection
                productEntity={product}
                temporaryMediaObjectsFieldname={"temporaryFilesToCombine"}
                mediaObjectCombinationRequest={mediaObjectCombinationRequests.enforcementOrder}
                setIsBlocked={setUploadIsBlocked}
                isBlocked={uploadIsBlocked}
                product={product.name}
                productEntityId={product.id}
                updateAction={updateAction}
                fieldName={"enforcementOrderDocument"}
                postSubmitCallback={postSubmitCallback}
                description={"Vollstreckungsbescheid Teildatei"}
                headline={"Vollstreckungsbescheid"}
                helperText={"Bitte laden Sie hier Ihren Vollstreckungsbescheid vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden."}
              />
            </Grid>
          )}
          {product.defenseProcessStatus === "lawsuit" && (
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FileUploadSection
                productEntity={product}
                temporaryMediaObjectsFieldname={"temporaryFilesToCombine"}
                mediaObjectCombinationRequest={mediaObjectCombinationRequests.lawsuit}
                setIsBlocked={setUploadIsBlocked}
                isBlocked={uploadIsBlocked}
                product={product.name}
                productEntityId={product.id}
                updateAction={updateAction}
                fieldName={"lawsuitDocument"}
                postSubmitCallback={postSubmitCallback}
                description={"Klage Teildatei"}
                headline={"Klage Gegenseite"}
                helperText={"Bitte laden Sie hier die Ihnen zugegangene Klage vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden."}
              />
            </Grid>
          )}
          {product.defenseProcessStatus === "reminder" && (
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant={"h5"}>Unterlagen zur Forderung gegen Sie</Typography>
              <p>
                Wenn Ihnen diese vorliegen, laden Sie hier bitte Unterlagen hoch, aus denen die Forderung gegen Sie entnommen werden kann. Zum Beispiel einen
                erhaltenen Brief oder den Screenshot einer E-Mail mit der Forderung. Wenn Ihnen nichts vorliegt, klicken Sie einfach auf “Weiter”.
              </p>
              <MultipleFileUpload
                product={product}
                fieldname={"reminderDocument"}
                description={"Forderungsunterlagen vom " + moment().format("DD.MM.YYYY")}
                electronicFileFolderPath={"Außergerichtliches Verfahren"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "reminderDocument")}
                fetchMediaObjects={fetchMediaObjectCollection}
              />
            </Grid>
          )}
          {product.defenseProcessStatus === "debtCollection" && (
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <Typography variant={"h5"}>Unterlagen zur Forderung gegen Sie</Typography>
              <p>
                Wenn Ihnen diese vorliegen, laden Sie hier bitte Unterlagen hoch, aus denen die Forderung gegen Sie entnommen werden kann. Zum Beispiel einen
                erhaltenen Brief oder den Screenshot einer E-Mail mit der Forderung. Wenn Ihnen nichts vorliegt, klicken Sie einfach auf “Weiter”.
              </p>
              <MultipleFileUpload
                product={product}
                fieldname={"debtCollectionDocument"}
                description={"Forderungsunterlagen vom " + moment().format("DD.MM.YYYY")}
                electronicFileFolderPath={"Außergerichtliches Verfahren"}
                mediaObjectCollection={filterMediaObjectsByFieldname(mediaObjectCollection, "debtCollectionDocument")}
                fetchMediaObjects={fetchMediaObjectCollection}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default CaseSpecificDocumentUploadSection;
